// Stock Study API 서비스
// 프록시 경로: /stock-study-api → http://localhost:8009

const BASE_URL = '/stock-study-api/api/v1';
const TIMEOUT = 10000;

// 인증 토큰 가져오기
const getAuthToken = () => {
  return localStorage.getItem('jwtToken');
};

// API 요청 기본 메서드
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const url = `${BASE_URL}${endpoint}`;

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

  try {
    const response = await fetch(url, {
      method: 'GET',
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // 204 No Content
    if (response.status === 204) {
      return { success: true, data: null };
    }

    const data = await response.json();

    if (!response.ok) {
      const errorCode = data?.error?.code || 'API_ERROR';
      const errorMessage = data?.error?.message || `HTTP ${response.status}`;
      const err = new Error(errorMessage);
      err.code = errorCode;
      err.status = response.status;
      throw err;
    }

    return { success: true, data: data.data || data };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      const err = new Error('요청 시간이 초과되었습니다.');
      err.code = 'REQUEST_TIMEOUT';
      throw err;
    }

    throw error;
  }
};

// ─────────────────────────────────────────
// Templates
// ─────────────────────────────────────────

export const templateApi = {
  // GET /templates - 템플릿 목록
  list: () => apiRequest('/templates'),

  // POST /templates - 템플릿 생성
  create: (data) =>
    apiRequest('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // GET /templates/:id - 템플릿 상세
  get: (id) => apiRequest(`/templates/${id}`),

  // PUT /templates/:id - 템플릿 수정
  update: (id, data) =>
    apiRequest(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // DELETE /templates/:id - 템플릿 삭제
  delete: (id) =>
    apiRequest(`/templates/${id}`, {
      method: 'DELETE',
    }),
};

// ─────────────────────────────────────────
// Studies (그룹 컨테이너)
// ─────────────────────────────────────────

export const studyApi = {
  // GET /studies - 스터디 목록 (필터/페이지네이션)
  list: (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.template_id) query.set('template_id', params.template_id);
    if (params.tags) query.set('tags', params.tags);
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    return apiRequest(`/studies${qs ? `?${qs}` : ''}`);
  },

  // POST /studies - 스터디 생성
  create: (data) =>
    apiRequest('/studies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // GET /studies/:id - 스터디 상세
  get: (id) => apiRequest(`/studies/${id}`),

  // PUT /studies/:id - 스터디 수정
  update: (id, data) =>
    apiRequest(`/studies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // DELETE /studies/:id - 스터디 삭제
  delete: (id) =>
    apiRequest(`/studies/${id}`, {
      method: 'DELETE',
    }),
};

// ─────────────────────────────────────────
// Study Entries (개별 분석 기록)
// ─────────────────────────────────────────

export const entryApi = {
  // GET /studies/:studyId/entries - 엔트리 목록
  list: (studyId, params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.date_from) query.set('date_from', params.date_from);
    if (params.date_to) query.set('date_to', params.date_to);
    if (params.order) query.set('order', params.order);
    const qs = query.toString();
    return apiRequest(`/studies/${studyId}/entries${qs ? `?${qs}` : ''}`);
  },

  // POST /studies/:studyId/entries - 엔트리 생성
  create: (studyId, data) =>
    apiRequest(`/studies/${studyId}/entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // GET /studies/:studyId/entries/:entryId - 엔트리 상세
  get: (studyId, entryId) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}`),

  // PUT /studies/:studyId/entries/:entryId - 엔트리 수정
  update: (studyId, entryId, data) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // DELETE /studies/:studyId/entries/:entryId - 엔트리 삭제
  delete: (studyId, entryId) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}`, {
      method: 'DELETE',
    }),
};

// ─────────────────────────────────────────
// Comments (엔트리 단위)
// ─────────────────────────────────────────

export const commentApi = {
  // GET /studies/:studyId/entries/:entryId/comments - 댓글 목록
  list: (studyId, entryId, params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    const qs = query.toString();
    return apiRequest(`/studies/${studyId}/entries/${entryId}/comments${qs ? `?${qs}` : ''}`);
  },

  // POST /studies/:studyId/entries/:entryId/comments - 댓글 작성
  create: (studyId, entryId, data) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // PATCH /studies/:studyId/entries/:entryId/comments/:commentId - 댓글 수정
  update: (studyId, entryId, commentId, data) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // DELETE /studies/:studyId/entries/:entryId/comments/:commentId - 댓글 삭제
  delete: (studyId, entryId, commentId) =>
    apiRequest(`/studies/${studyId}/entries/${entryId}/comments/${commentId}`, {
      method: 'DELETE',
    }),
};
